import { AlertTriangle, ArrowRight, CircleDollarSign, RefreshCw, UserRoundCog } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { MetricRange } from '@/schemas/commons';

import { DashboardLoading } from './Dashboard.loading';
import { useDashboardData } from './Dashboard.requests';
import { styles } from './Dashboard.styles';
import { deltaClass, sparkPoints } from './Dashboard.utils';

const RANGES: MetricRange[] = ['24h', '7d', '30d'];

export const Dashboard = () => {
	const { t } = useTranslation('translation', { keyPrefix: 'dashboard' });
	const [range, setRange] = useState<MetricRange>('24h');
	const { metrics, recent, isLoading, isError } = useDashboardData(range);

	if (isLoading) return <DashboardLoading />;
	if (isError || !metrics.data || !recent.data) {
		return (
			<main className={styles.content}>
				<p className="text-[13px] text-error">{t('ERROR_LOAD')}</p>
			</main>
		);
	}

	const m = metrics.data;
	const conversations = recent.data.items;

	return (
		<>
			<header className={styles.topbar}>
				<nav className="flex items-center gap-3xs text-[12px] text-subtle">
					<span className="font-medium text-default">{t('BREADCRUMB')}</span>
				</nav>
				<div className="flex items-center gap-sm">
					<span className="hidden items-center gap-3xs text-[11px] leading-none text-subtle md:inline-flex">
						{t('UPDATED_AGO')}
						<span className="ml-3xs font-medium tabular-nums text-default">12 s</span>
					</span>
					<span className="h-sm w-[1px] bg-subtle" />
					<button
						type="button"
						onClick={() => metrics.refetch()}
						className="inline-flex items-center gap-3xs text-[11px] text-subtle transition-colors hover:text-default"
					>
						<RefreshCw className="h-sm w-sm" />
						{t('REFRESH')}
					</button>
				</div>
			</header>

			<main className={styles.content}>
				<header className="flex flex-wrap items-end justify-between gap-md">
					<div>
						<p className={styles.pageEyebrow}>{t('EYEBROW')}</p>
						<h1 className={styles.pageH1}>{t('TITLE')}</h1>
						<p className={styles.pageSub}>{t('SUBTITLE')}</p>
					</div>
					<div className={styles.rangeToggle}>
						{RANGES.map((r) => (
							<button
								key={r}
								type="button"
								onClick={() => setRange(r)}
								className={`${styles.rangeBtn} ${range === r ? styles.rangeBtnActive : styles.rangeBtnIdle}`}
							>
								{r}
							</button>
						))}
					</div>
				</header>

				<dl className={styles.statGrid}>
					{[
						{ key: 'TURNS', stat: m.turns, sparkColor: '#22C488' },
						{ key: 'ERRORS', stat: m.errors, sparkColor: '#22C488' },
						{ key: 'THREADS', stat: m.activeThreads, sparkColor: '#BABDBF' },
						{ key: 'TOOL_CALLS', stat: m.toolCalls, sparkColor: '#22C488' },
					].map(({ key, stat, sparkColor }) => (
						<div key={key} className={styles.stat}>
							<svg className={styles.statSpark} viewBox="0 0 60 24" preserveAspectRatio="none">
								<polyline
									points={sparkPoints(stat.spark)}
									fill="none"
									stroke={sparkColor}
									strokeWidth="1.5"
									strokeLinejoin="round"
								/>
							</svg>
							<dt className={styles.statLabel}>{t(`STAT_${key}`)}</dt>
							<dd className={styles.statValue}>{stat.formatted}</dd>
							<span className={deltaClass(stat)}>{stat.deltaLabel}</span>
						</div>
					))}
					<div className={styles.stat}>
						<svg className={styles.statSpark} viewBox="0 0 60 24" preserveAspectRatio="none">
							<polyline
								points={sparkPoints(m.cost.spark)}
								fill="none"
								stroke="#812DF2"
								strokeWidth="1.5"
								strokeLinejoin="round"
							/>
						</svg>
						<dt className={styles.statLabel}>
							<CircleDollarSign className="h-sm w-sm text-purple-700" />
							{t('STAT_COST')}
						</dt>
						<dd className={styles.statValueBrand}>{m.cost.formatted}</dd>
						<span className={styles.deltaFlat}>
							{t('STAT_COST_MONTH')} <span className="font-semibold text-default">{m.cost.monthToDateFormatted}</span>
						</span>
					</div>
				</dl>

				<section className="flex flex-col gap-sm">
					<header className="flex items-baseline justify-between">
						<div>
							<h2 className={styles.sectionH}>{t('RECENT_TITLE')}</h2>
							<p className={styles.sectionSub}>{t('RECENT_SUBTITLE')}</p>
						</div>
						<Link
							to="/conversations"
							className="inline-flex items-center gap-3xs text-[12px] text-default transition-colors hover:text-purple-700"
						>
							{t('SEE_ALL')}
							<ArrowRight className="h-sm w-sm" />
						</Link>
					</header>

					<table className="w-full text-left text-[13px]">
						<thead className="border-b border-subtle">
							<tr>
								<th scope="col" className={`${styles.tableHeader} px-sm py-xs`}>
									{t('COL_THREAD')}
								</th>
								<th scope="col" className={`${styles.tableHeader} px-sm py-xs`}>
									{t('COL_USER')}
								</th>
								<th scope="col" className={`${styles.tableHeader} px-sm py-xs`}>
									{t('COL_COMPANY')}
								</th>
								<th scope="col" className={`${styles.tableHeader} px-sm py-xs`}>
									{t('COL_COUNTRY')}
								</th>
								<th scope="col" className={`${styles.tableHeader} px-sm py-xs text-right`}>
									{t('COL_MESSAGES')}
								</th>
								<th scope="col" className={`${styles.tableHeader} px-sm py-xs text-right`}>
									{t('COL_COST')}
								</th>
								<th scope="col" className={`${styles.tableHeader} px-sm py-xs`}>
									{t('COL_ACTIVITY')}
								</th>
								<th scope="col" className={`${styles.tableHeader} px-sm py-xs`}>
									{t('COL_STATUS')}
								</th>
							</tr>
						</thead>
						<tbody>
							{conversations.map((conv) => (
								<tr
									key={conv.threadId}
									onClick={() => window.location.assign(`/conversations/${conv.threadId}`)}
									className="cursor-pointer border-b border-subtle/50 transition-colors hover:bg-grey-25"
								>
									<td className="px-sm py-xs">
										<code className="font-mono text-[11px] text-default">{conv.threadId}</code>
									</td>
									<td className="px-sm py-xs">
										<div className="flex items-center gap-2xs">
											<span className={styles.userAvatar}>{conv.userInitials}</span>
											{conv.userName}
										</div>
									</td>
									<td className={`${styles.tableCellMuted} px-sm py-xs`}>{conv.companyName}</td>
									<td className={`${styles.tableCellMuted} px-sm py-xs`}>{conv.country}</td>
									<td className={`${styles.tableCell} px-sm py-xs text-right tabular-nums`}>{conv.messageCount}</td>
									<td
										className={`px-sm py-xs text-right tabular-nums ${conv.status === 'tool_error' ? 'font-semibold text-error' : styles.tableCell}`}
									>
										${conv.costUsd.toFixed(3)}
									</td>
									<td className={`${styles.tableCellMuted} px-sm py-xs tabular-nums`}>{conv.lastActivity}</td>
									<td className="px-sm py-xs">
										{conv.status === 'active' && (
											<span className={styles.statusActive}>
												<span className={styles.dotSuccess} />
												{t('STATUS_ACTIVE')}
											</span>
										)}
										{conv.status === 'closed' && <span className={styles.statusClosed}>{t('STATUS_CLOSED')}</span>}
										{conv.status === 'handoff' && (
											<span className={styles.statusHandoff}>
												<UserRoundCog className="h-sm w-sm" />
												{t('STATUS_HANDOFF')}
											</span>
										)}
										{conv.status === 'tool_error' && (
											<span className={styles.statusError}>
												<AlertTriangle className="h-sm w-sm" />
												{t('STATUS_TOOL_ERROR')}
											</span>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</section>
			</main>
		</>
	);
};
